import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useTestimonials } from "@/hooks/useTestimonials";

const Testimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const { testimonials, loading, error } = useTestimonials();

  // Auto-rotate testimonials (skip when empty to avoid `% 0` -> NaN)
  useEffect(() => {
    if (testimonials.length === 0) return;
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Keep the active index valid if the list size changes or went NaN
  useEffect(() => {
    if (
      testimonials.length > 0 &&
      (!Number.isInteger(currentTestimonial) ||
        currentTestimonial < 0 ||
        currentTestimonial >= testimonials.length)
    ) {
      setCurrentTestimonial(0);
    }
  }, [testimonials.length, currentTestimonial]);

  // Always read from a safe, in-range index
  const safeIndex =
    Number.isInteger(currentTestimonial) &&
    currentTestimonial >= 0 &&
    currentTestimonial < testimonials.length
      ? currentTestimonial
      : 0;
  const activeTestimonial = testimonials[safeIndex];

  return (
    <section className="py-20 relative bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest-green mb-6">
              What They Say
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-soft-saffron to-copper-brown rounded-full mx-auto mb-6"></div>
            <p className="text-xl text-copper-brown max-w-3xl mx-auto">
              Voices from our community of practitioners
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-soft-saffron mx-auto"></div>
              <p className="mt-4 text-copper-brown">Loading testimonials...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <p className="text-copper-brown">Showing default testimonials</p>
            </div>
          )}

          {/* Testimonials Carousel */}
          {!loading && activeTestimonial && (
            <div className="relative">
              <Card className="bg-cloud-white shadow-2xl border-0 max-w-4xl mx-auto">
                <CardContent className="p-12">
                  <div
                    className="text-center animate-gentle-fade"
                    key={safeIndex}
                  >
                    {/* Quote */}
                    <div className="mb-8">
                      <span className="text-6xl text-soft-saffron font-serif leading-none">
                        "
                      </span>
                      <p className="text-xl md:text-2xl text-copper-brown leading-relaxed italic max-w-3xl mx-auto">
                        {activeTestimonial.writeup}
                      </p>
                      <span className="text-6xl text-soft-saffron font-serif leading-none">
                        "
                      </span>
                    </div>

                    {/* Author */}
                    <div className="flex items-center justify-center space-x-4">
                      <img
                        src={activeTestimonial.image}
                        alt={activeTestimonial.name}
                        className="w-16 h-16 min-w-16 min-h-16 max-w-16 max-h-16 rounded-full object-cover border-4 border-soft-saffron/30 flex-shrink-0"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src =
                            "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face";
                        }}
                      />
                      <div className="text-left">
                        <h4 className="font-heading text-xl font-semibold text-forest-green">
                          {activeTestimonial.name}
                        </h4>
                        <p className="text-copper-brown">
                          {activeTestimonial.occupation || "Community Member"}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Navigation Dots */}
              <div className="flex justify-center space-x-3 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === safeIndex
                        ? "bg-soft-saffron scale-125"
                        : "bg-misty-sage hover:bg-copper-brown"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
